import { useState, useRef, useMemo, MouseEventHandler, useEffect } from "react";
// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
// Materials
import { GridCellProps, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
// Custom components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import { Nullable } from "@/Common/interfaces/types";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { CommonSalesTableProps } from "@/Washouts/types/commons";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";

// Types and constants
import { Issue } from "@/Common/types/tables";
import { DATE_FORMAT } from "@/Constants/date";

function getInitialColumns(reference = "", issues: Issue[] = []): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			width: 63,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}, reference),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateSlashShortYear),
			renderCell: (params) => (
				<TypographyCell reference={reference} params={params} issues={issues} hints={{ noWrap: true }}>
					{isFooterCell(params) ? "Chargebacks Total" : null}
				</TypographyCell>
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargebackDate",
			headerName: "C.B. Date",
			width: 63,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateSlashShortYear),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "type",
			headerName: "Type",
			width: 65,
			minWidth: 60,
			maxWidth: 85,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			width: 65,
			minWidth: 60,
			maxWidth: 85,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargebackInvoiceAmount",
			headerName: "Invoice Amount",
			width: 85,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
		{
			field: "saleGross",
			headerName: "Sale Gross",
			width: 85,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
		{
			field: "unit",
			headerName: "Units",
			width: 55,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="account" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
		{
			field: "chargebackAmount",
			headerName: "C.B. Gross",
			width: 85,
			type: "number",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
		{
			field: "chargebackCommission",
			headerName: "C.B. Comm.",
			width: 85,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
	];
}

function ChargesTable(props: Readonly<CommonSalesTableProps>) {
	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id ?? "", // _id
			columnReference: event.currentTarget?.dataset.field ?? "", // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const handleChangeIssues = (color: Nullable<string>) => {
		let currentIssues = [...issues];
		if (color) {
			let isPresent = false;
			for (const issue of currentIssues) {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					issue.color = color;
					isPresent = true;
					break;
				}
			}

			if (!isPresent) {
				currentIssues.push({
					color,
					tableReference: props?.issuesReference,
					rowReference: activeIssue?.current?.rowReference,
					columnReference: activeIssue?.current?.columnReference,
				});
			}
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	useMemo(() => {
		setTotalColumns(getInitialColumns(props?.issuesReference, issues));
	}, [props?.issuesReference, issues]);

	useEffect(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => {
					return {
						...prevState,
						filter: { ...filter, ...cleanFilter },
					};
				});
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		setIssues(props.issues || []);

		return () => {
			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef]);

	if (!totalColumns) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	return (
		<>
			<DataSmartTable
				{...props.tableProps}
				apiRef={apiRef}
				columns={totalColumns}
				rows={props.data ?? []}
				hideFooter
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				columnVisibilityModel={{
					_id: false,
				}}
				initialState={{
					aggregation: {
						model: {
							chargebackInvoiceAmount: "sum",
							saleGross: "sum",
							unit: "sum",
							chargebackAmount: "sum",
							chargebackCommission: "sum",
						},
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							hide: false,
							label: props.title ?? "Chargebacks",
							sx: { mb: "15px" },
						},
					},
					noRowsOverlay: {
						title: "No Chargebacks Data",
					},
					cell: getCell(),
				}}
				customProps={{}}
			/>
			{props.addIssues && (
				<AddCellIssues
					open={openAddIssues}
					anchorEl={anchorAddIssues}
					onCloseAction={() => {
						setOpenAddIssues(false);
						setAnchorAddIssues(null);
					}}
					onChangeAction={handleChangeIssues}
				/>
			)}
		</>
	);
}

export default function ChargebacksTable(props: Readonly<CommonSalesTableProps>) {
	if (props.loading) {
		return <TableLoader smallTitle content sx={{ padding: "15px 10px 0px 10px" }} />;
	}

	return <>{props.dataPresent && <ChargesTable {...props} />}</>;
}
